import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ConversationModel, ConversationRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['conversations']

type ConversationFilters = {
  campaign_id?: number,
  subject_id?: number,
  subject_type?: string,
}

type UseConversationsOptions = {
  filters?: ConversationFilters,
  performHttpRequests?: boolean,
  requestOptions?: ConversationRequestOptions,
}

function useConversations(options: UseConversationsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
    subject_id: filterSubjectId,
    subject_type: filterSubjectType,
  } = filters

  const {
    updatedEntities: { conversations: conversationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { conversations } = useSelector(reduxState => reduxState.entities)

  const filteredConversations = useMemo(() => {
    const filtered = Object.values(conversations).filter((conversation: ConversationModel) => {
      const { campaign_id, subject_id, subject_type } = conversation

      const matchCampaign = matchFilterNumber(campaign_id, filterCampaignId)

      // Match Subject
      const matchSubjectId = matchFilterNumber(subject_id, filterSubjectId)
      const matchSubjectType = matchFilterString(subject_type, filterSubjectType)
      const matchSubject = matchSubjectId && matchSubjectType

      return matchCampaign && matchSubject
    })

    return sortArrayBy(filtered, 'desc', (conversation) => {
      // @ts-expect-error TS2339
      const { last_comment } = conversation

      return last_comment || conversation?.id
    })
  }, [conversationsUpdatedAt, JSON.stringify(filters)])

  const filteredConversationsCount = filteredConversations.length
  const hasFilteredConversations = !!filteredConversationsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredConversationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingConversations } = useReduxAction(
    'conversations',
    'loadConversations',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredConversations,
    filteredConversationsCount,
    hasFilteredConversations,
    loading: loadingConversations,
  }
}

export default useConversations
